import { useAuth0 } from '@auth0/auth0-react';
import { OgEvent } from '@opengov/analytics';
import { useEffect, useState } from 'react';
import { useLoggerWithContext } from '../services/logger/loggerContext';
import { runSessionEndFanout } from '../services/mountedModulesRegistry';
import { queryClient } from '../services/queryClient';
import { getOgAnalytics, isOgAnalyticsEnabled } from '../services/analytics/ogAnalytics';
import { signInAppState } from '../services/auth/signInReturnPath';
import { SIGNED_OUT_PATH } from '../utils/auth0Config';

/**
 * Upper bound on the analytics step of logout. `reset()` already stops waiting
 * for its flush after 1.5s inside the SDK; this is PSP's own backstop, so a
 * promise that never settles cannot keep the Auth0 redirect from running and
 * leave the session live.
 */
const ANALYTICS_LOGOUT_TIMEOUT_MS = 3000;

export const useAuth = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    getIdTokenClaims,
    error,
  } = useAuth0();

  const logger = useLoggerWithContext();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      void getAccessTokenSilently()
        .then((token: string) => {
          setToken(token);
          logger.info('Access token retrieved successfully', {
            auth_event: 'token_retrieved',
            user_id: user?.sub,
          });
        })
        .catch((err) => {
          setToken(null);
          logger.error('Failed to retrieve access token', {
            auth_event: 'token_retrieval_failed',
            error_message: err.message,
            error_name: err.error,
            error_description: err.error_description,
            user_id: user?.sub,
          });
          throw err;
        });
    } else {
      setToken(null);
    }
  }, [isAuthenticated, getAccessTokenSilently, logger, user?.sub]);

  const login = () => {
    logger.info('Initiating login redirect', {
      auth_event: 'login_redirect_initiated',
      current_path: window.location.pathname,
    });
    void loginWithRedirect({ appState: signInAppState() });
  };

  // Fan out to mounted modules' `onSessionEnd` hooks BEFORE the Auth0
  // redirect, so modules can flush session-scoped state (user-keyed caches,
  // IndexedDB, app-specific storage). Auth0's logout() triggers a browser
  // navigation that does not run React useEffect cleanups — without explicit
  // fan-out, modules never get a chance to run logout-specific cleanup. Modules
  // that opted in to `sessionEndWhenUnmounted` are reached even when not mounted.
  const logoutUser = async (): Promise<void> => {
    logger.info('User logout initiated', {
      auth_event: 'logout_initiated',
      user_id: user?.sub,
    });

    try {
      await runSessionEndFanout({
        onError: (appId, err) => {
          logger.warn('Module session end failed during logout fan-out', {
            auth_event: 'logout_session_end_error',
            app_id: appId,
            error_message: err instanceof Error ? err.message : String(err),
          });
        },
        onStorageError: (err) => {
          logger.warn('Session-end apps could not be read or cleared during logout', {
            auth_event: 'logout_session_end_storage_error',
            error_message: err instanceof Error ? err.message : String(err),
          });
        },
      });
    } catch (err) {
      // Defensive — runSessionEndFanout uses allSettled internally so it should
      // never reject, but a logged warning beats a swallowed exception.
      logger.warn('Logout fan-out raised unexpectedly', {
        auth_event: 'logout_fanout_error',
        error_message: err instanceof Error ? err.message : String(err),
      });
    }

    // ── Analytics ─────────────────────────────────────────────────────
    // `reset()` emits nothing itself — it is a client-side identity clear — so
    // the event has to be explicit for logout to be visible.
    //
    // `reset()` flushes before clearing, so the event above is still attributed
    // to the departing user. Awaited because Auth0's logout() navigates away.
    if (isOgAnalyticsEnabled()) {
      try {
        const analytics = getOgAnalytics();
        // `OgEvent.SignedOut` rather than the literal: the name belongs to the
        // SDK's closed vocabulary, and `signed_in` in `recordSignIn()` is drawn
        // from the same place so the pair cannot drift apart. `method` matches
        // what General Ledger sends, so one query covers both apps.
        analytics.track(OgEvent.SignedOut, { method: 'auth0' });
        let timeout: ReturnType<typeof setTimeout> | undefined;
        await Promise.race([
          analytics.reset(),
          new Promise<void>((resolve) => {
            timeout = setTimeout(resolve, ANALYTICS_LOGOUT_TIMEOUT_MS);
          }),
        ]).finally(() => clearTimeout(timeout));
      } catch (err) {
        logger.warn('Analytics logout handling failed', {
          auth_event: 'logout_analytics_error',
          error_message: err instanceof Error ? err.message : String(err),
        });
      }
    }

    // Belt-and-suspenders: clear entitlement cache before the Auth0 redirect.
    // The redirect clears in-memory React Query state anyway, but explicit removal
    // ensures correctness for any auth flow that doesn't trigger a full page reload
    // (popup login, silent re-auth, future SPA-mode changes).
    queryClient.removeQueries({ queryKey: ['entitledEntities'] });
    queryClient.removeQueries({ queryKey: ['activeEntityEntitledApplications'] });
    queryClient.removeQueries({ queryKey: ['entityProductEntitlements'] });
    queryClient.removeQueries({ queryKey: ['activeEntityClaims'] });

    // Awaited so a rejection reaches the caller, which shows sign-out failed rather than pending.
    await logout({
      logoutParams: {
        returnTo: `${window.location.origin}${SIGNED_OUT_PATH}`,
      },
    });
  };

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout: logoutUser,
    getAccessTokenSilently,
    getIdTokenClaims,
    token,
  };
};
